<template>
  <thead class="text-left text-xs font-medium text-label" :class="headClasses">
    <slot />
    <tr ref="tableHeaderRow">
      <th
        v-if="selectAll || selectOne"
        class="h-13 px-4 pl-5 font-medium break-words"
      >
        <div class="flex items-center space-x-1-5">
          <lf-checkbox
            v-if="showHeadSelectAll && !selectOne"
            :value="isSelectedAllRows"
            :model-value="isSelectedAllRows"
            name="select_all"
            :disabled="disabledCheckboxes"
            no-margin
            @update:model-value="handleToggleAllCheckboxes"
          >
            <span v-if="selectTitle" class="ml-4 text-gray-400">
              {{ selectTitle }}
            </span>
          </lf-checkbox>
          <div
            v-if="
              hasSelectModeOption &&
              !isSelectModeEnabled &&
              canDisableSelectMode
            "
          >
            <button
              v-tooltip="$t('COMMON.MULTISELECT_OFF')"
              :data-cy="`multiselectButton-${isSelectModeEnabled}`"
              class="mt-[2px] h-8 w-8 cursor-pointer rounded-sm hover:bg-grey-hover"
            >
              <icon-base
                :icon="IconXCircle"
                @click="emit('multiselect:change', !isSelectModeEnabled)"
              />
            </button>
          </div>
        </div>
      </th>
      <template
        v-for="(column, index) in columns"
        :key="`column-head-${column.key}`"
      >
        <th
          class="lf-table-header relative h-13 px-5 pl-5 font-medium"
          :class="[{ 'whitespace-nowrap': headerNoWrap }, column.headerClass]"
          :data-cy="`table-header-column-${column.key}`"
        >
          <div class="th-wrapper">
            <loader :is-loading="column.isSubColumnLoading ?? false" />
            <div class="flex w-full items-center space-x-2">
              <div
                v-if="hasSelectModeOption && index === 0 && isSelectModeEnabled"
              >
                <button
                  v-tooltip="$t('COMMON.MULTISELECT')"
                  :data-cy="`multiselectButton-${isSelectModeEnabled}`"
                  class="h-8 w-8 cursor-pointer rounded-sm hover:bg-grey-hover"
                >
                  <icon-base
                    :icon="IconCheckSquare"
                    @click="emit('multiselect:change', !isSelectModeEnabled)"
                  />
                </button>
              </div>
              <div class="flex w-full justify-between space-x-4">
                <div
                  class="relative flex cursor-pointer items-start"
                  :class="flexJustifyContentClass(column.headerClass)"
                >
                  <span v-if="!column.filterComponent?.staticProps?.filterIcon">
                    {{ column.label }}
                  </span>
                  <table-filters-v2
                    v-else
                    :column="column"
                    :toggle-filter="toggleFilter === index + 1"
                    :initial-component-render="
                      Boolean(column.filterComponent?.initialRender)
                    "
                    :data-cy="`table-header-filter-${column.key}`"
                    @click.prevent.stop="toggleFilter = index + 1"
                    @close-filter="closeFilter"
                  />
                </div>
                <button
                  v-if="shouldShowPinButton(index)"
                  v-tooltip="getPinTooltip(index)"
                  class="cursor-pointer"
                  data-cy="pinButton"
                  @click="handlePinClick(index)"
                >
                  <icon-base
                    height="12"
                    width="12"
                    :icon="IconPinSecondary"
                    :class="getPinIconClasses(index)"
                  />
                </button>
              </div>
            </div>
            <button
              v-if="column?.subColumns"
              class="th-arrow-button absolute -top-0.5 -right-7 rounded-md border border-gray-200 bg-white"
              :data-cy="'sub-columns-button-' + column.key"
              @click="toggleSubColumns(column.key)"
            >
              <icon-base
                :icon="IconArrowRight"
                class="mt-0.5"
                :class="{
                  'rotate-180 transform': isSubColumnVisible(column.key),
                }"
              />
            </button>
          </div>
        </th>
        <template v-if="isSubColumnVisible(column.key)">
          <th
            v-for="(subColumn, subIndex) in column.subColumns"
            :key="`subColumn-head-${subColumn.key}`"
            class="h-13 bg-gray-50 px-4 pl-5 font-medium"
            :class="{
              'whitespace-nowrap': headerNoWrap,
            }"
          >
            <div class="relative flex cursor-pointer items-start">
              <span v-if="!subColumn.filterComponent?.staticProps?.filterIcon">
                {{ subColumn.label }}
              </span>
              <table-filters-v2
                v-else
                :column="subColumn"
                :toggle-filter="toggleFilter === subIndex + columns.length + 1"
                @click.prevent.stop="
                  toggleFilter = subIndex + columns.length + 1
                "
                @close-filter="closeFilter"
              />
            </div>
          </th>
        </template>
      </template>
    </tr>
  </thead>
</template>
<script setup lang="ts">
import { computed, inject, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { SubColumnsVisibilityKey } from "@/helpers/symbols";
import IconArrowRight from "@/components/icons/IconArrowRight.vue";

import LfCheckbox from "../inputs/LfCheckbox.vue";
import TableFiltersV2 from "./TableFiltersV2.vue";
import IconCheckSquare from "@/components/icons/IconCheckSquare.vue";
import IconPinSecondary from "@/components/icons/IconPinSecondary.vue";
import IconXCircle from "@/components/icons/IconXCircle.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { TableColumnV2 } from "@/models/applications";

const {
  columns,
  headerNoWrap = true,
  selectAll = false,
  selectOne = false,
  selectTitle = "",
  showHeadSelectAll = true,
  headClasses = "",
  setHeaderMinWidths = false,
  isLoading = false,
  isSelectedAllRows = false,
  pinEnabled,
  pinLastEnabled = false,
  pinOption = false,
  disabledCheckboxes = false,
  hasSelectModeOption = false,
  isSelectModeEnabled = false,
  canDisableSelectMode = true,
  showLastColumnPin = false,
} = defineProps<{
  columns: TableColumnV2[];
  headerNoWrap?: boolean;
  selectAll?: boolean;
  selectOne?: boolean;
  selectTitle?: string;
  showHeadSelectAll?: boolean;
  headClasses?: string;
  setHeaderMinWidths?: boolean;
  isLoading?: boolean;
  isSelectedAllRows?: boolean;
  pinEnabled: boolean;
  pinLastEnabled?: boolean;
  pinOption?: boolean;
  disabledCheckboxes?: boolean;
  hasSelectModeOption?: boolean;
  isSelectModeEnabled?: boolean;
  canDisableSelectMode?: boolean;
  showLastColumnPin?: boolean;
}>();

const emit = defineEmits<{
  toggleAllCheckboxes: [value: boolean];
  toggleSubColumn: [payload: string];
  "click:pin": [value: boolean];
  "click:pin-last": [value: boolean];
  "multiselect:change": [value: boolean];
}>();

const { t } = useI18n();
const tableHeaderRow = ref<HTMLElement | null>(null);
const toggleFilter = ref(0);

const subColumnsVisibility = inject(
  SubColumnsVisibilityKey,
  ref<Record<string, boolean>>({})
);

const isSubColumnVisible = (key: string) => {
  return !!subColumnsVisibility.value[key];
};

const shouldShowPinButton = computed(
  () => (index: number) =>
    pinOption &&
    (index === 0 || (index === columns.length - 1 && showLastColumnPin))
);

const getPinTooltip = computed(() => (index: number) => {
  const isPinned = index === 0 ? pinEnabled : pinLastEnabled;
  return isPinned
    ? t("DEALS.TABLE_HEADERS.UNPIN_COLUMN")
    : t("DEALS.TABLE_HEADERS.PIN_COLUMN");
});

const handlePinClick = (index: number) => {
  if (index === 0) {
    emit("click:pin", !pinEnabled);
  } else {
    emit("click:pin-last", !pinLastEnabled);
  }
};

const getPinIconClasses = computed(() => (index: number) => ({
  "text-blue-600": index === 0 ? pinEnabled : pinLastEnabled,
}));

const toggleSubColumns = (key: string) => {
  emit("toggleSubColumn", key);
};

function toggleHeaderStyles(remove = false): void {
  tableHeaderRow.value
    ?.querySelectorAll<HTMLElement>(".lf-table-header")
    .forEach((header: HTMLElement) => {
      header.style.minWidth = remove ? "" : `${header.offsetWidth}px`;
    });
}

const handleToggleAllCheckboxes = (value: boolean) => {
  emit("toggleAllCheckboxes", value);
};

const closeFilter = () => (toggleFilter.value = 0);

const flexJustifyContentClass = (headerClass: string | undefined) => {
  return headerClass === "text-right" ? "justify-end w-full" : "";
};

watch(
  () => isLoading,
  (loading) => {
    if (setHeaderMinWidths) {
      toggleHeaderStyles(!loading);
    }
  }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.lf-table-header:last-child .th-wrapper {
  @apply flex items-center gap-2-5;
}

.lf-table-header:last-child .th-arrow-button {
  @apply static;
}
</style>
